import React from 'react';
import { Typography, Divider, List, Row, Col, Empty, theme } from 'antd';

import { PlayerInfo } from 'components';
import CricketMatchData from 'pages/Admin/strategies/CricketMatchData';

import useAdmin from "hooks/useAdmin";
import useScreenSize from "hooks/useScreenSize";

import RecentForm from './RecentForm';

const { Text } = Typography;
const { useToken } = theme;

const ComparisonStats = ({
    initialPlayer,
    initialPlayerStats,
    initialPlayerResults,
    comparisonPlayer,
    comparisonPlayerStats,
    comparisonPlayerResults
}) => {

    const { token } = useToken();
    const { adminData } = useAdmin();
    const [{ isMobile }] = useScreenSize();

    const showAvatar = adminData.sportIsCricket() && !isMobile;

    const hasSelection = !!initialPlayer || !!comparisonPlayer;

    const highlightStyle = {
        background: token.colorPrimary,
        color: token.colorTextLightSolid,
        padding: '2px 8px',
        borderRadius: token.borderRadiusSM,
    };

    const renderValue = (stats, key) => (stats ? stats[key].toLocaleString() : '-');

    const getValueWinner = (initial, comparison, lowerIsBetter = false) => {
        const initialValue = Number(initial);
        const comparisonValue = Number(comparison);

        if (Number.isNaN(initialValue) || Number.isNaN(comparisonValue) || initialValue === comparisonValue) {
            return null;
        }

        const initialIsBetter = lowerIsBetter
            ? initialValue < comparisonValue
            : initialValue > comparisonValue;
        return initialIsBetter ? 'initial' : 'comparison';
    };

    const getWinner = (field) => {
        if (!initialPlayerStats || !comparisonPlayerStats) {
            return null;
        }

        return getValueWinner(initialPlayerStats[field.key], comparisonPlayerStats[field.key], field.lowerIsBetter);
    };

    const renderHighlightedValue = (side, winner, value) => (
        <Text strong style={winner === side ? highlightStyle : undefined}>{value}</Text>
    );

    const renderGeneralSection = () => {
        const totalPointsWinner = getValueWinner(initialPlayer?.totalPoints, comparisonPlayer?.totalPoints);
        const selectedByWinner = getValueWinner(initialPlayer?.selectedBy, comparisonPlayer?.selectedBy);

        const generalFields = [
            {
                key: 'player',
                label: 'Player',
                initial: initialPlayer ? <PlayerInfo player={initialPlayer} showAvatar={showAvatar} /> : '-',
                comparison: comparisonPlayer ? <PlayerInfo player={comparisonPlayer} showAvatar={showAvatar} /> : '-'
            },
            {
                key: 'recentForm',
                label: 'Recent Form',
                initial: initialPlayer ? <RecentForm results={initialPlayerResults} /> : '-',
                comparison: comparisonPlayer ? <RecentForm results={comparisonPlayerResults} /> : '-'
            },
            {
                key: 'totalPoints',
                label: 'Total Points',
                initial: initialPlayer ? renderHighlightedValue('initial', totalPointsWinner, initialPlayer.totalPoints.toLocaleString()) : '-',
                comparison: comparisonPlayer ? renderHighlightedValue('comparison', totalPointsWinner, comparisonPlayer.totalPoints.toLocaleString()) : '-'
            },
            {
                key: 'selectedBy',
                label: 'Selected By',
                initial: initialPlayer ? renderHighlightedValue('initial', selectedByWinner, `${initialPlayer.selectedBy.toLocaleString()}%`) : '-',
                comparison: comparisonPlayer ? renderHighlightedValue('comparison', selectedByWinner, `${comparisonPlayer.selectedBy.toLocaleString()}%`) : '-'
            }
        ];

        return (
            <>
                <Divider titlePlacement="left">General</Divider>
                <List
                    style={{ marginBottom: 24 }}
                    bordered
                    dataSource={generalFields}
                    renderItem={field => (
                        <List.Item>
                            <Row align="middle" style={{ width: '100%' }}>
                                <Col xs={7} sm={8} lg={9} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    {field.initial}
                                </Col>
                                <Col xs={10} sm={8} lg={6} style={{ textAlign: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{field.label}</Text>
                                </Col>
                                <Col xs={7} sm={8} lg={9} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                    {field.comparison}
                                </Col>
                            </Row>
                        </List.Item>
                    )}
                    />
            </>
        );
    };

    const renderSection = (title, fields) => (
        <>
            <Divider titlePlacement="left">{title}</Divider>
            <List
                style={{ marginBottom: 24 }}
                bordered
                dataSource={fields}
                renderItem={field => {
                    const winner = getWinner(field);
                    return (
                        <List.Item>
                            <Row align="middle" style={{ width: '100%' }}>
                                <Col xs={7} sm={8} lg={9} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Text strong style={winner === 'initial' ? highlightStyle : undefined}>
                                        {renderValue(initialPlayerStats, field.key)}
                                    </Text>
                                </Col>
                                <Col xs={10} sm={8} lg={6} style={{ textAlign: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{field.label}</Text>
                                </Col>
                                <Col xs={7} sm={8} lg={9} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                    <Text strong style={winner === 'comparison' ? highlightStyle : undefined}>
                                        {renderValue(comparisonPlayerStats, field.key)}
                                    </Text>
                                </Col>
                            </Row>
                        </List.Item>
                    );
                }}
                />
        </>
    );

    if (!hasSelection) {
        return <Empty description="Select two players to compare their stats." />
    }

    const statGroups = new CricketMatchData().getComparisonStatGroups();

    return (
        <>
            {renderGeneralSection()}
            {statGroups.map(group => (
                <React.Fragment key={group.title}>
                    {renderSection(group.title, group.fields)}
                </React.Fragment>
            ))}
        </>
    );
};

export default ComparisonStats;
